/**
 * BeezFy Premium Product Gallery V3.0 - Complete Rewrite
 * All controls working, proper zoom toggle, lightbox fix
 */

class BeezfyPremiumGallery {
  constructor(element) {
    this.gallery = element;
    if (!this.gallery) return;
    this.sectionId = this.gallery.dataset.sectionId || this.gallery.closest('[data-section-id]')?.dataset.sectionId || null;

    // Settings from data attributes with admin customization
    this.settings = {
      enableZoom: this.gallery.dataset.enableZoom !== 'false',
      zoomType: this.gallery.dataset.zoomType || 'lens',
      enableLightbox: this.gallery.dataset.enableLightbox !== 'false',
      enableSwipe: this.gallery.dataset.enableSwipe !== 'false',
      showCounter: this.gallery.dataset.showCounter === 'true',
      animationIntensity: this.gallery.dataset.animationIntensity || 'medium',
      objectFit: this.gallery.dataset.objectFit || 'contain', // New setting
      mediaAspectRatio: this.gallery.dataset.aspectRatio || '4:5',
      thumbsPosition: this.gallery.dataset.thumbsPosition || 'left'
    };

    // State
    // Con "Video in evidenza" la slide attiva a pagina caricata non e' la prima:
    // il server marca gia' quella giusta, qui allineo lo stato del componente
    // altrimenti il primo click su avanti/indietro salterebbe di una posizione.
    this.currentIndex = parseInt(this.gallery.dataset.initialIndex, 10) || 0;
    this.totalMedia = 0;
    this.isZoomed = false;
    this.lightboxInstance = null;

    this.init();
  }

  init() {

    // Get existing elements
    this.mainElement = this.gallery.querySelector('[data-gallery-main]');
    this.thumbsElement = this.gallery.querySelector('[data-gallery-thumbs]');
    this.viewportElement = this.mainElement ? this.mainElement.querySelector('.beezfy-gallery__viewport') : null;
    this.mediaElements = this.gallery.querySelectorAll('.beezfy-gallery__media');
    this.thumbElements = this.gallery.querySelectorAll('.beezfy-gallery__thumb');
    this.dotsElements = this.gallery.querySelectorAll('.beezfy-gallery__dot');

    this.totalMedia = this.mediaElements.length;

    if (this.totalMedia === 0) {
      console.warn('BeezFy Gallery V3: No media elements found');
      return;
    }

    // Apply object-fit setting to all images
    this.applyObjectFit();

    // Bind events
    this.bindEvents();

    // Setup global event delegation for lightbox close button
    this.setupGlobalEventDelegation();

    // Show first media
    this.showMedia(0);

    // Initialize zoom if enabled
    if (this.settings.enableZoom) {
      this.initializeZoom();
    }

    // Setup animation intensity
    this.setupAnimations();

    // Sync thumbnail rail height with main media
    this.syncThumbsHeight();
    this.boundSyncThumbsHeight = this.syncThumbsHeight.bind(this);
    window.addEventListener('resize', this.boundSyncThumbsHeight);
    window.addEventListener('orientationchange', this.boundSyncThumbsHeight);
    window.addEventListener('load', this.boundSyncThumbsHeight);
    this.boundSectionUnload = this.onSectionUnload.bind(this);
    document.addEventListener('shopify:section:unload', this.boundSectionUnload);

  }

  setupGlobalEventDelegation() {
    // Use event delegation to catch ALL clicks on lightbox close buttons
    document.addEventListener('click', (e) => {
      // Check if clicked element or its parent is the close button
      const closeBtn = e.target.closest('.beezfy-gallery__lightbox-close');
      if (closeBtn && this.lightboxInstance && this.lightboxInstance.classList.contains('active')) {
        e.preventDefault();
        e.stopPropagation();
        this.closeLightbox();
      }
    }, true); // Use capture phase
  }

  applyObjectFit() {
    const images = this.gallery.querySelectorAll('.beezfy-gallery__image');
    images.forEach(img => {
      img.style.objectFit = this.settings.objectFit;
    });
  }

  setupAnimations() {
    const duration = {
      'none': '0ms',
      'light': '200ms',
      'medium': '300ms',
      'heavy': '500ms'
    }[this.settings.animationIntensity] || '300ms';

    this.mediaElements.forEach(media => {
      media.style.transitionDuration = duration;
    });
  }

  bindEvents() {
    // Thumbnail clicks
    if (this.thumbElements.length > 0) {
      this.thumbElements.forEach((thumb, index) => {
        thumb.addEventListener('click', (e) => {
          e.preventDefault();
          this.showMedia(index);
        });
      });
    }

    // Navigation buttons
    const prevBtn = this.gallery.querySelector('[data-gallery-prev]');
    const nextBtn = this.gallery.querySelector('[data-gallery-next]');

    if (prevBtn) {
      prevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.previousMedia();
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.nextMedia();
      });
    }

    // Thumbnail navigation arrows
    this.initThumbsNavigation();

    // Mobile dots
    if (this.dotsElements.length > 0) {
      this.dotsElements.forEach((dot, index) => {
        dot.addEventListener('click', (e) => {
          e.preventDefault();
          this.showMedia(index);
        });
      });
    }

    // Video indicators - navigate to video slide (both mobile and desktop)
    const videoIndicators = this.gallery.querySelectorAll('[data-video-indicator]');
    videoIndicators.forEach(videoIndicator => {
      videoIndicator.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const videoSlide = parseInt(videoIndicator.getAttribute('data-video-slide') || '0');
        this.showMedia(videoSlide);
      });
    });

    // Keyboard navigation
    document.addEventListener('keydown', (e) => {
      // Only work if gallery is in view
      const rect = this.gallery.getBoundingClientRect();
      const inView = rect.top < window.innerHeight && rect.bottom > 0;

      if (!inView) return;

      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.previousMedia();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.nextMedia();
      } else if (e.key === 'Escape' && this.lightboxInstance) {
        this.closeLightbox();
      }
    });

    // Touch/swipe support
    if (this.settings.enableSwipe && this.mainElement) {
      this.setupSwipe();
    }

    // Lightbox button
    const lightboxBtn = this.gallery.querySelector('[data-lightbox-toggle]');
    if (lightboxBtn && this.settings.enableLightbox) {
      lightboxBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.openLightbox();
      });
    }

    // Zoom button with proper toggle
    const zoomBtn = this.gallery.querySelector('[data-zoom-toggle]');
    if (zoomBtn && this.settings.enableZoom) {
      zoomBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggleZoom();
        this.updateZoomButton();
      });
    }
  }

  setupSwipe() {
    let touchStartX = 0;
    let touchStartY = 0;
    let touchEndX = 0;
    let touchEndY = 0;

    this.mainElement.addEventListener('touchstart', (e) => {
      touchStartX = e.changedTouches[0].screenX;
      touchStartY = e.changedTouches[0].screenY;
    }, { passive: true });

    this.mainElement.addEventListener('touchend', (e) => {
      touchEndX = e.changedTouches[0].screenX;
      touchEndY = e.changedTouches[0].screenY;
      this.handleSwipe(touchStartX, touchStartY, touchEndX, touchEndY);
    }, { passive: true });

    // Mouse drag support for desktop
    let mouseDown = false;
    let mouseStartX = 0;

    this.mainElement.addEventListener('mousedown', (e) => {
      mouseDown = true;
      mouseStartX = e.clientX;
      e.preventDefault();
    });

    document.addEventListener('mouseup', (e) => {
      if (mouseDown) {
        mouseDown = false;
        const mouseEndX = e.clientX;
        const diff = mouseStartX - mouseEndX;

        if (Math.abs(diff) > 50) {
          if (diff > 0) {
            this.nextMedia();
          } else {
            this.previousMedia();
          }
        }
      }
    });
  }

  handleSwipe(startX, startY, endX, endY) {
    const threshold = 50;
    const diffX = startX - endX;
    const diffY = startY - endY;

    // Only handle horizontal swipes
    if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > threshold) {
      if (diffX > 0) {
        this.nextMedia();
      } else {
        this.previousMedia();
      }
    }
  }

  showMedia(index) {
    if (index < 0 || index >= this.totalMedia) return;


    // Update media visibility
    this.mediaElements.forEach((media, i) => {
      const isActive = i === index;
      media.classList.toggle('active', isActive);
      media.setAttribute('aria-hidden', !isActive);

      // Smooth transition
      if (isActive) {
        media.style.opacity = '1';
        media.style.visibility = 'visible';
      } else {
        media.style.opacity = '0';
        media.style.visibility = 'hidden';
      }
    });

    // Update thumbnails
    if (this.thumbElements.length > 0) {
      this.thumbElements.forEach((thumb, i) => {
        thumb.classList.toggle('active', i === index);
        thumb.setAttribute('aria-selected', i === index);
      });

      // Scroll active thumb into view
      if (this.thumbElements[index]) {
        this.thumbElements[index].scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest'
        });
      }
    }

    // Update dots
    if (this.dotsElements.length > 0) {
      this.dotsElements.forEach((dot, i) => {
        dot.classList.toggle('active', i === index);
        dot.setAttribute('aria-selected', i === index);
      });
    }

    // Update counter
    const counterCurrent = this.gallery.querySelector('[data-current]');
    if (counterCurrent) {
      counterCurrent.textContent = index + 1;
    }

    this.currentIndex = index;

    // Lazy load next image
    if (index < this.totalMedia - 1) {
      const nextMedia = this.mediaElements[index + 1];
      const nextImg = nextMedia?.querySelector('img[loading="lazy"]');
      if (nextImg && !nextImg.complete) {
        nextImg.loading = 'eager';
      }
    }

    // Update lightbox if open
    if (this.lightboxInstance && this.lightboxInstance.classList.contains('active')) {
      this.updateLightbox();
    }

    // Disable zoom button if current slide is a video
    const zoomBtn = this.gallery.querySelector('[data-zoom-toggle]');
    if (zoomBtn) {
      const activeMedia = this.mediaElements[index];
      const hasVideo = activeMedia?.querySelector('video');

      if (hasVideo) {
        zoomBtn.style.opacity = '0.3';
        zoomBtn.style.cursor = 'not-allowed';
        zoomBtn.style.pointerEvents = 'none';
      } else {
        zoomBtn.style.opacity = '0.95';
        zoomBtn.style.cursor = 'pointer';
        zoomBtn.style.pointerEvents = 'auto';
      }
    }

    this.syncThumbsHeight();
  }

  previousMedia() {
    const newIndex = this.currentIndex > 0 ? this.currentIndex - 1 : this.totalMedia - 1;
    this.showMedia(newIndex);
  }

  nextMedia() {
    const newIndex = this.currentIndex < this.totalMedia - 1 ? this.currentIndex + 1 : 0;
    this.showMedia(newIndex);
  }

  syncThumbsHeight() {
    if (!this.thumbsElement) return;

    if (this.settings.thumbsPosition === 'bottom') {
      this.thumbsElement.style.removeProperty('height');
      this.thumbsElement.style.removeProperty('max-height');
      this.thumbsElement.style.removeProperty('min-height');
      return;
    }

    if (window.innerWidth < 768) {
      this.thumbsElement.style.removeProperty('height');
      this.thumbsElement.style.removeProperty('max-height');
      this.thumbsElement.style.removeProperty('min-height');
      return;
    }

    const viewport = this.viewportElement || this.gallery.querySelector('.beezfy-gallery__viewport');
    if (!viewport) return;

    window.requestAnimationFrame(() => {
      const viewportHeight = viewport.offsetHeight;
      if (!viewportHeight) return;
      this.thumbsElement.style.height = `${viewportHeight}px`;
      this.thumbsElement.style.maxHeight = `${viewportHeight}px`;
      this.thumbsElement.style.minHeight = `${viewportHeight}px`;
    });
  }

  onSectionUnload(event) {
    const unloadedSectionId = event?.detail?.sectionId;
    if (!unloadedSectionId) return;
    if (this.sectionId && unloadedSectionId !== this.sectionId) return;

    if (this.boundSyncThumbsHeight) {
      window.removeEventListener('resize', this.boundSyncThumbsHeight);
      window.removeEventListener('orientationchange', this.boundSyncThumbsHeight);
      window.removeEventListener('load', this.boundSyncThumbsHeight);
    }

    if (this.boundSectionUnload) {
      document.removeEventListener('shopify:section:unload', this.boundSectionUnload);
    }
  }

  initializeZoom() {
    if (this.settings.zoomType === 'lens') {
      this.initializeLensZoom();
    } else if (this.settings.zoomType === 'click') {
      this.initializeClickZoom();
    }
  }

  initializeLensZoom() {
    // Create lens if it doesn't exist
    let lens = this.gallery.querySelector('[data-zoom-lens]');
    if (!lens) {
      lens = document.createElement('div');
      lens.className = 'beezfy-gallery__zoom-lens';
      lens.setAttribute('data-zoom-lens', '');
      lens.hidden = true;
      lens.style.cssText = `
        position: absolute;
        width: 100px;
        height: 100px;
        border: 2px solid #000;
        border-radius: 50%;
        pointer-events: none;
        z-index: 100;
        background-repeat: no-repeat;
      `;
      this.mainElement.appendChild(lens);
    }

    this.mediaElements.forEach((mediaElement) => {
      const img = mediaElement.querySelector('.beezfy-gallery__image');
      if (!img) return;

      const wrapper = img.parentElement;

      wrapper.addEventListener('mouseenter', (e) => {
        if (!this.isZoomed) return;

        const activeMedia = this.mediaElements[this.currentIndex];
        if (mediaElement !== activeMedia) return;

        lens.hidden = false;
        lens.style.backgroundImage = `url(${img.src})`;
        lens.style.backgroundSize = '300%';
      });

      wrapper.addEventListener('mousemove', (e) => {
        if (!this.isZoomed || lens.hidden) return;

        const rect = wrapper.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        // Position lens
        lens.style.left = `${x - 50}px`;
        lens.style.top = `${y - 50}px`;

        // Update background position
        const bgX = (x / rect.width) * 100;
        const bgY = (y / rect.height) * 100;
        lens.style.backgroundPosition = `${bgX}% ${bgY}%`;
      });

      wrapper.addEventListener('mouseleave', () => {
        lens.hidden = true;
      });
    });
  }

  initializeClickZoom() {
    this.mediaElements.forEach((mediaElement) => {
      const img = mediaElement.querySelector('.beezfy-gallery__image');
      if (!img) return;

      img.style.cursor = 'zoom-in';
      img.style.transition = 'transform 0.3s ease';

      img.addEventListener('click', (e) => {
        if (e.target !== img) return;

        this.isZoomed = !this.isZoomed;

        if (this.isZoomed) {
          img.style.transform = 'scale(2)';
          img.style.cursor = 'zoom-out';
        } else {
          img.style.transform = 'scale(1)';
          img.style.cursor = 'zoom-in';
        }

        this.updateZoomButton();
      });
    });
  }

  toggleZoom() {
    // Check if current slide is a video - don't allow zoom on videos
    const activeMedia = this.mediaElements[this.currentIndex];
    const hasVideo = activeMedia?.querySelector('video');

    if (hasVideo) {
      console.log('Zoom not available for video slides');
      return;
    }

    this.isZoomed = !this.isZoomed;

    // For ALL zoom types, apply visual zoom to the image when button is clicked
    const img = activeMedia?.querySelector('.beezfy-gallery__image');

    if (img) {
      if (this.isZoomed) {
        img.style.transform = 'scale(2)';
        img.style.cursor = 'zoom-out';
        img.style.transformOrigin = 'center center';
        img.style.transition = 'transform 0.3s ease';

        // Allow panning when zoomed
        if (this.settings.zoomType === 'click') {
          this.enablePanning(img);
        }
      } else {
        img.style.transform = 'scale(1)';
        img.style.cursor = 'zoom-in';

        if (this.settings.zoomType === 'click') {
          this.disablePanning(img);
        }
      }
    }

  }

  enablePanning(img) {
    const wrapper = img.parentElement;
    let isPanning = false;
    let startX = 0;
    let startY = 0;
    let scrollLeft = 0;
    let scrollTop = 0;

    const mouseDown = (e) => {
      if (!this.isZoomed) return;
      isPanning = true;
      startX = e.pageX - wrapper.offsetLeft;
      startY = e.pageY - wrapper.offsetTop;
      scrollLeft = wrapper.scrollLeft;
      scrollTop = wrapper.scrollTop;
      img.style.cursor = 'grabbing';
    };

    const mouseMove = (e) => {
      if (!isPanning || !this.isZoomed) return;
      e.preventDefault();
      const x = e.pageX - wrapper.offsetLeft;
      const y = e.pageY - wrapper.offsetTop;
      const walkX = (x - startX) * 2;
      const walkY = (y - startY) * 2;
      wrapper.scrollLeft = scrollLeft - walkX;
      wrapper.scrollTop = scrollTop - walkY;
    };

    const mouseUp = () => {
      isPanning = false;
      if (this.isZoomed) {
        img.style.cursor = 'grab';
      }
    };

    wrapper.addEventListener('mousedown', mouseDown);
    wrapper.addEventListener('mousemove', mouseMove);
    wrapper.addEventListener('mouseup', mouseUp);
    wrapper.addEventListener('mouseleave', mouseUp);

    // Store handlers for removal
    img._panHandlers = { mouseDown, mouseMove, mouseUp };
  }

  disablePanning(img) {
    const wrapper = img.parentElement;
    if (img._panHandlers) {
      wrapper.removeEventListener('mousedown', img._panHandlers.mouseDown);
      wrapper.removeEventListener('mousemove', img._panHandlers.mouseMove);
      wrapper.removeEventListener('mouseup', img._panHandlers.mouseUp);
      wrapper.removeEventListener('mouseleave', img._panHandlers.mouseUp);
      delete img._panHandlers;
    }
  }

  updateZoomButton() {
    const zoomBtn = this.gallery.querySelector('[data-zoom-toggle]');
    if (!zoomBtn) return;

    const svg = zoomBtn.querySelector('svg');
    if (!svg) return;

    // Change icon based on zoom state
    if (this.isZoomed) {
      // Minus icon
      svg.innerHTML = `
        <circle cx="11" cy="11" r="8"/>
        <path d="M21 21l-4.35-4.35M8 11h6"/>
      `;
      zoomBtn.setAttribute('aria-label', 'Zoom out');
    } else {
      // Plus icon
      svg.innerHTML = `
        <circle cx="11" cy="11" r="8"/>
        <path d="M21 21l-4.35-4.35M11 8v6M8 11h6"/>
      `;
      zoomBtn.setAttribute('aria-label', 'Zoom in');
    }
  }

  openLightbox() {
    // Always check if lightbox exists in DOM (might have been removed)
    if (!this.lightboxInstance || !document.body.contains(this.lightboxInstance)) {
      this.lightboxInstance = null; // Reset if it was removed from DOM
    }

    // Create lightbox if it doesn't exist
    if (!this.lightboxInstance) {
      this.lightboxInstance = document.createElement('div');
      this.lightboxInstance.className = 'beezfy-gallery__lightbox';
      // Store reference to this instance for inline onclick
      window._currentGalleryInstance = this;

      this.lightboxInstance.innerHTML = `
        <div class="beezfy-gallery__lightbox-inner">
          <button class="beezfy-gallery__lightbox-close" aria-label="Close" onclick="window._currentGalleryInstance.closeLightbox(); return false;">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
          <div class="beezfy-gallery__lightbox-content"></div>
          <button class="beezfy-gallery__lightbox-prev" aria-label="Previous">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
          </button>
          <button class="beezfy-gallery__lightbox-next" aria-label="Next">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </button>
          ${this.settings.showCounter ? `
            <div class="beezfy-gallery__lightbox-counter">
              <span data-lightbox-current>${this.currentIndex + 1}</span> /
              <span data-lightbox-total>${this.totalMedia}</span>
            </div>
          ` : ''}
        </div>
      `;
      document.body.appendChild(this.lightboxInstance);

      // Add styles if not present
      if (!document.querySelector('#beezfy-lightbox-styles')) {
        const style = document.createElement('style');
        style.id = 'beezfy-lightbox-styles';
        style.textContent = `
          .beezfy-gallery__lightbox {
            display: none;
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0, 0, 0, 0.95);
            z-index: 10000;
            animation: fadeIn 0.3s ease;
          }
          .beezfy-gallery__lightbox.active {
            display: flex;
            align-items: center;
            justify-content: center;
          }
          .beezfy-gallery__lightbox-inner {
            position: relative;
            width: 90%;
            height: 90%;
            max-width: 1200px;
            max-height: 800px;
          }
          .beezfy-gallery__lightbox-content {
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          .beezfy-gallery__lightbox-content img {
            max-width: 100%;
            max-height: 100%;
            object-fit: ${this.settings.objectFit};
          }
          .beezfy-gallery__lightbox-content video {
            max-width: 100%;
            max-height: 100%;
            object-fit: contain;
          }
          .beezfy-gallery__lightbox-close {
            position: absolute;
            top: 20px;
            right: 20px;
            background: transparent;
            border: none;
            color: #fff;
            cursor: pointer;
            z-index: 10001;
            padding: 10px;
          }
          .beezfy-gallery__lightbox-prev,
          .beezfy-gallery__lightbox-next {
            position: absolute;
            top: 50%;
            transform: translateY(-50%);
            background: transparent;
            border: none;
            color: #fff;
            cursor: pointer;
            padding: 20px;
          }
          .beezfy-gallery__lightbox-prev {
            left: 20px;
          }
          .beezfy-gallery__lightbox-next {
            right: 20px;
          }
          .beezfy-gallery__lightbox-counter {
            position: absolute;
            bottom: 20px;
            left: 50%;
            transform: translateX(-50%);
            color: #fff;
            font-size: 14px;
          }
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
        `;
        document.head.appendChild(style);
      }

      // Bind navigation buttons (close is handled by global delegation)
      const prevBtn = this.lightboxInstance.querySelector('.beezfy-gallery__lightbox-prev');
      if (prevBtn) {
        prevBtn.onclick = () => {
          this.previousMedia();
          this.updateLightbox();
        };
      }

      const nextBtn = this.lightboxInstance.querySelector('.beezfy-gallery__lightbox-next');
      if (nextBtn) {
        nextBtn.onclick = () => {
          this.nextMedia();
          this.updateLightbox();
        };
      }

      // Close on background click - use onclick for reliability
      this.lightboxInstance.onclick = (e) => {
        if (e.target === this.lightboxInstance || e.target.classList.contains('beezfy-gallery__lightbox-inner')) {
          this.closeLightbox();
        }
      };

      // Add swipe support to lightbox
      let touchStartX = 0;
      let touchStartY = 0;

      this.lightboxInstance.addEventListener('touchstart', (e) => {
        touchStartX = e.changedTouches[0].screenX;
        touchStartY = e.changedTouches[0].screenY;
      }, { passive: true });

      this.lightboxInstance.addEventListener('touchend', (e) => {
        const touchEndX = e.changedTouches[0].screenX;
        const touchEndY = e.changedTouches[0].screenY;

        const deltaX = touchEndX - touchStartX;
        const deltaY = touchEndY - touchStartY;

        // Swipe threshold
        if (Math.abs(deltaX) > 50 && Math.abs(deltaX) > Math.abs(deltaY)) {
          if (deltaX > 0) {
            // Swipe right - previous
            this.previousMedia();
            this.updateLightbox();
          } else {
            // Swipe left - next
            this.nextMedia();
            this.updateLightbox();
          }
        }
      }, { passive: true });
    }

    // Update and show lightbox
    this.updateLightbox();
    this.lightboxInstance.classList.add('active');
    document.body.style.overflow = 'hidden';

    // Add ESC key listener
    this.escHandler = (e) => {
      if (e.key === 'Escape') {
        this.closeLightbox();
      }
    };
    document.addEventListener('keydown', this.escHandler);
  }

  updateLightbox() {
    if (!this.lightboxInstance) return;

    const activeMedia = this.mediaElements[this.currentIndex];
    const img = activeMedia?.querySelector('.beezfy-gallery__image');
    const video = activeMedia?.querySelector('video');
    const content = this.lightboxInstance.querySelector('.beezfy-gallery__lightbox-content');

    if (video) {
      // Show video in lightbox
      const videoHtml = video.outerHTML;
      content.innerHTML = videoHtml;

      // Get the newly inserted video and play it
      const lightboxVideo = content.querySelector('video');
      if (lightboxVideo) {
        lightboxVideo.play().catch(() => {
          // Autoplay might be blocked
        });
      }
    } else if (img) {
      // Show image in lightbox
      const safeAlt = (img.alt || '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
      content.innerHTML = `<img src="${img.src}" alt="${safeAlt}">`;
    }

    // Update counter
    const currentSpan = this.lightboxInstance.querySelector('[data-lightbox-current]');
    if (currentSpan) {
      currentSpan.textContent = this.currentIndex + 1;
    }
  }

  closeLightbox() {
    if (this.lightboxInstance) {
      this.lightboxInstance.classList.remove('active');
      document.body.style.overflow = '';

      // Remove ESC handler
      if (this.escHandler) {
        document.removeEventListener('keydown', this.escHandler);
        this.escHandler = null;
      }

    } else {
      console.warn('BeezFy Gallery V3: No lightbox instance to close');
    }
  }

  // Public methods for external control
  getCurrentIndex() {
    return this.currentIndex;
  }

  getTotalMedia() {
    return this.totalMedia;
  }

  goToMedia(index) {
    this.showMedia(index);
  }

  setObjectFit(fit) {
    this.settings.objectFit = fit;
    this.applyObjectFit();
  }

  initThumbsNavigation() {
    const prevBtn = this.gallery.querySelector('[data-thumbs-prev]');
    const nextBtn = this.gallery.querySelector('[data-thumbs-next]');

    if (!prevBtn || !nextBtn || !this.thumbsElement) return;

    // Click handlers
    prevBtn.addEventListener('click', (e) => {
      e.preventDefault();
      this.scrollThumbs('prev');
    });

    nextBtn.addEventListener('click', (e) => {
      e.preventDefault();
      this.scrollThumbs('next');
    });

    // Update button states on scroll
    this.thumbsElement.addEventListener('scroll', () => {
      this.updateThumbsButtons(prevBtn, nextBtn);
    });

    // Initial state
    setTimeout(() => this.updateThumbsButtons(prevBtn, nextBtn), 100);
  }

  scrollThumbs(direction) {
    if (!this.thumbsElement) return;

    const isHorizontal = this.gallery.dataset.thumbsPosition === 'bottom';
    const scrollAmount = isHorizontal
      ? this.thumbsElement.clientWidth * 0.7
      : this.thumbsElement.clientHeight * 0.7;

    if (isHorizontal) {
      const current = this.thumbsElement.scrollLeft;
      this.thumbsElement.scrollTo({
        left: direction === 'next' ? current + scrollAmount : current - scrollAmount,
        behavior: 'smooth'
      });
    } else {
      const current = this.thumbsElement.scrollTop;
      this.thumbsElement.scrollTo({
        top: direction === 'next' ? current + scrollAmount : current - scrollAmount,
        behavior: 'smooth'
      });
    }
  }

  updateThumbsButtons(prevBtn, nextBtn) {
    if (!this.thumbsElement || !prevBtn || !nextBtn) return;

    const isHorizontal = this.gallery.dataset.thumbsPosition === 'bottom';

    if (isHorizontal) {
      const atStart = this.thumbsElement.scrollLeft <= 1;
      const atEnd = this.thumbsElement.scrollLeft >=
        (this.thumbsElement.scrollWidth - this.thumbsElement.clientWidth - 1);

      prevBtn.disabled = atStart;
      nextBtn.disabled = atEnd;
    } else {
      const atStart = this.thumbsElement.scrollTop <= 1;
      const atEnd = this.thumbsElement.scrollTop >=
        (this.thumbsElement.scrollHeight - this.thumbsElement.clientHeight - 1);

      prevBtn.disabled = atStart;
      nextBtn.disabled = atEnd;
    }
  }
}

// Initialize galleries
function initializeGalleries() {
  const galleries = document.querySelectorAll('[data-beezfy-premium-gallery]');

  galleries.forEach(gallery => {
    if (!gallery._galleryInitialized) {
      try {
        const instance = new BeezfyPremiumGallery(gallery);
        gallery._galleryInitialized = true;
        gallery._galleryInstance = instance;
      } catch (error) {
        console.error('BeezFy Gallery V3: Initialization error:', error);
      }
    }
  });
}

// Initialize on various events
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeGalleries);
} else {
  initializeGalleries();
}

// Also try on load and with delay
window.addEventListener('load', initializeGalleries);
setTimeout(initializeGalleries, 1000);

// Export for global access
window.BeezfyPremiumGallery = BeezfyPremiumGallery;
